import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import { useUserRoles } from '@apple/features/auth/hooks/useUserRoles';
import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { getLocationApprovalQueue } from '@apple/features/location/api/location-approval';
import type {
	LocationApprovalFilters,
	LocationApprovalRequest,
} from '@apple/features/location/models/location-approval';
import { locationApprovalQueryKeys } from '@apple/features/location/queries/location-approval.queries';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

const defaultFilters: LocationApprovalFilters = {};
const defaultSorting: SortingState = [{ id: 'date', desc: true }];

export const Route = createFileRoute('/_authed/_admin/approvals/location')({
	component: LocationApprovals,
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'AppleBrandedPrograms.Security.Features.LocationApprovalManagement',
		});

		return {
			approvalLocationsQueryOptions: queryOptions({
				queryKey: locationApprovalQueryKeys.queue,
				queryFn: () => getLocationApprovalQueue(),
				placeholderData: keepPreviousData,
				throwOnError: true,
				initialData: [],
			}),
		};
	},
	loader: ({ context: { queryClient, approvalLocationsQueryOptions } }) => {
		void queryClient.prefetchQuery(approvalLocationsQueryOptions);
	},
});

function LocationApprovals() {
	const { t } = useTranslation('location-approval', {
		nsMode: 'fallback',
	});
	const { approvalLocationsQueryOptions } = Route.useRouteContext();

	const locationsQuery = useQuery(approvalLocationsQueryOptions);
	const userRoles = useUserRoles();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();

	const tableState = useTableState<LocationApprovalRequest, LocationApprovalFilters>({
		search,
		navigate,
		defaultFilters: defaultFilters,
		defaultSorting: defaultSorting,
		fieldMap: [],
	});

	const table = useTable({
		features,
		data: locationsQuery.data,
		enableFilters: true,
		...tableState,
		enableColumnFilters: true,
		enableSorting: true,
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'role':
						return new Map(
							!userRoles.loading && !userRoles.error
								? userRoles.roles?.map(x => [x.name, 1])
								: [],
						);
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
		columns: [
			{
				enableColumnFilter: false,
				enableGlobalFilter: false,
				accessorKey: 'userId',
				header: t('headers.requests'),
				cell: ({ row }) => (
					<Link
						to='/approvals/location/$userId'
						params={{ userId: row?.original.userId.toString() }}
						title={t('table.viewRequest')}
						children={t('table.viewRequest')}
					/>
				),
			},
			{
				accessorKey: 'username',
				header: t('headers.username'),
				filter: {
					group: t('filters.labels.locationInfo'),
					variant: 'text',
				},
			},
			{
				enableColumnFilter: false,
				enableGlobalFilter: false,
				accessorKey: 'firstName',
				header: t('headers.firstName'),
			},
			{
				enableColumnFilter: false,
				enableGlobalFilter: false,
				accessorKey: 'lastName',
				header: t('headers.lastName'),
			},
			{
				id: 'role',
				accessorKey: 'role',
				header: t('headers.role'),
				filter: {
					group: t('filters.labels.locationInfo'),
					variant: 'select',
				},
			},
			{
				enableColumnFilter: false,
				enableGlobalFilter: false,
				accessorKey: 'requests',
				header: t('headers.locations'),
			},
			{
				accessorKey: 'date',
				header: t('headers.date'),
				enableSorting: true,
				sortFn: 'datetime',
				filter: {
					group: t('filters.labels.locationInfo'),
					variant: 'date-range',
				},
				cell: ({ row }) => <FormattedDate value={row.original.date} humanize />,
			},
		],
	});

	return (
		<TableLayout title={t('title.list')} table={table}>
			<DataTable table={table} loading={locationsQuery.isFetching} />
		</TableLayout>
	);
}
